import React from 'react';
import './Text.css';

/**
 * AURORA TEXT PRIMITIVE
 * 
 * A token-based typography component that never uses hardcoded styles
 * All styling comes from tokens.css
 * 
 * Variants: 'body', 'caption', 'overline'
 * Sizes: 'xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl'
 * Weights: 'normal', 'medium', 'semibold', 'bold'
 * Colors: 'primary', 'secondary', 'tertiary', 'inverse', 'error', 'success', 'warning'
 * 
 * @example
 * ```jsx
 * <Text>Default body text</Text>
 * <Text size="lg" weight="semibold">Large semibold text</Text>
 * <Text color="secondary" as="span">Secondary colored text</Text>
 * <Text variant="caption" color="tertiary">Small caption text</Text>
 * ```
 */

const Text = ({
  children,
  as: Component = 'p',
  variant = 'body',
  size,
  weight = 'normal',
  color = 'primary',
  align = 'left',
  transform,
  truncate = false,
  lineClamp,
  className = '',
  ...props
}) => {
  const classes = [
    'aurora-text',
    variant && `aurora-text--${variant}`,
    size && `aurora-text--size-${size}`,
    `aurora-text--weight-${weight}`,
    `aurora-text--color-${color}`,
    align !== 'left' && `aurora-text--align-${align}`,
    transform && `aurora-text--transform-${transform}`,
    truncate && 'aurora-text--truncate',
    lineClamp && `aurora-text--line-clamp-${lineClamp}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <Component className={classes} {...props}>
      {children}
    </Component>
  );
};

/**
 * HEADING COMPONENT
 * Convenience wrapper for heading elements
 */
const Heading = ({
  children,
  level = 1,
  size,
  weight = 'bold',
  color = 'primary',
  className = '',
  ...props
}) => {
  const Component = `h${level}`;
  
  // Default sizes for heading levels
  const defaultSizes = {
    1: '4xl',
    2: '3xl',
    3: '2xl',
    4: 'xl',
    5: 'lg',
    6: 'base'
  };
  
  const headingSize = size || defaultSizes[level];
  
  return (
    <Text
      as={Component}
      size={headingSize}
      weight={weight}
      color={color}
      className={`aurora-heading ${className}`}
      {...props}
    >
      {children}
    </Text>
  );
};

Text.displayName = 'Text';
Heading.displayName = 'Heading';

export { Text, Heading };
export default Text;
